import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import FormCheckbox from '../../components/Forms/FormCheckbox';
// import Icon from '../../components/UI/Icon'; // Não está sendo usado
import { SystemMessageDto, PromptParameterDto } from '../../services/applications/types';
import { 
  createApplicationPrompt, 
  getApplicationById,
  getPromptParameters,
  getUserStatuses,
  getPromptTools
} from '../../services/applications/applicationsService';

const PromptCreate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [applicationName, setApplicationName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [availableParameters, setAvailableParameters] = useState<PromptParameterDto[]>([]);
  const [selectedParameter, setSelectedParameter] = useState<string>('');
  
  // Form state
  const [prompt, setPrompt] = useState<SystemMessageDto>({
    message: '',
    system_message: '',
    user_status: '',
    application_id: id,
    is_active: true,
    temperature: 0.7,
    with_rag: false,
    tools: [],
    provider_name: 'ANTHROPIC', // Modelo padrão
    model_name: 'claude-3-haiku-20240307'  // Modelo padrão
  });
  const [userStatuses, setUserStatuses] = useState<string[]>([]);
  const [availableTools, setAvailableTools] = useState<string[]>([]);
  const [selectedTool, setSelectedTool] = useState<string>('');

  // Function to insert a template parameter at cursor position
  const insertParameter = () => {
    if (!selectedParameter) return;
    
    const textArea = document.getElementById('message') as HTMLTextAreaElement;
    if (!textArea) return;
    
    const cursorPosition = textArea.selectionStart;
    const textBefore = textArea.value.substring(0, cursorPosition);
    const textAfter = textArea.value.substring(cursorPosition);
    
    const newMessage = `${textBefore}{${selectedParameter}}${textAfter}`;
    
    setPrompt(prev => ({
      ...prev,
      message: newMessage
    }));
    
    // Reset the selection
    setSelectedParameter('');
    
    // Focus back on textarea (delayed to ensure state update)
    setTimeout(() => {
      textArea.focus();
      textArea.selectionStart = cursorPosition + selectedParameter.length + 2; // +2 for the braces
      textArea.selectionEnd = cursorPosition + selectedParameter.length + 2;
    }, 50);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!id || !auth.user) return;
      
      try {
        // Fetch data in parallel for better performance
        const [applicationData, parametersData, statusesData, toolsData] = await Promise.all([
          getApplicationById(auth.user, id),
          getPromptParameters(auth.user),
          getUserStatuses(auth.user),
          getPromptTools(auth.user)
        ]);
        
        setApplicationName(applicationData.name || 'Application');
        setAvailableParameters(parametersData);
        setUserStatuses(statusesData);
        setAvailableTools(toolsData);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Could not fetch application details');
      }
    };
    
    fetchInitialData();
  }, [id, auth.user]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!prompt.message || prompt.message.trim() === '') {
      errors.message = 'Message is required';
    }

    if (!prompt.provider_name) {
      errors.provider_name = 'Provider is required';
    }

    // API requer system_message, mas já estamos copiando de message
    if (!prompt.system_message || prompt.system_message.trim() === '') {
      prompt.system_message = prompt.message; // Preencher automaticamente se estiver vazio
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setPrompt(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!auth.user) {
      setError('You must be authenticated to create a prompt');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (!id) {
        throw new Error('Application ID is required');
      }
      
      // Criar o prompt e navegar de volta para a lista
      await createApplicationPrompt(auth.user, id, prompt);
      
      // Navigate back to prompts list
      navigate(`/applications/${id}/prompts`);
    } catch (err: any) {
      console.error('Error creating prompt:', err);
      setError(err.message || 'Failed to create prompt');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={`New System Message - ${applicationName}`}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: applicationName, to: `/applications/${id}` },
          { label: "System Messages", to: `/applications/${id}/prompts` },
          { label: "New" }
        ]}
      />
      
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4 flex items-start">
              <i className="fas fa-exclamation-circle mt-0.5 mr-3 text-red-500"></i>
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md border mb-6">
              <h2 className="text-xl font-semibold mb-4">Configurações Básicas</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormSelect
                    label="Status do Usuário"
                    id="user_status"
                    name="user_status"
                    value={prompt.user_status || ''}
                    onChange={handleInputChange}
                    error={formErrors.user_status}
                    options={[
                      { value: "", label: "Todos os Usuários (Default)" },
                      ...userStatuses.map(status => ({
                        value: status,
                        label: status === "NEW" ? "Usuários Novos" :
                               status === "ACTIVE" ? "Usuários Ativos" :
                               status === "INACTIVE" ? "Usuários Inativos" :
                               status === "BLOCKED" ? "Usuários Bloqueados" :
                               status === "ONBOARDING_IN_PROGRESS" ? "Em Onboarding" :
                               status // Fallback para status não mapeados
                      }))
                    ]}
                    leftIcon={<i className="fas fa-users text-gray-400 text-sm" />}
                  />
                </div>
                
                <div className="flex items-center h-full">
                  <FormCheckbox
                    id="is_active"
                    name="is_active"
                    label="Ativo"
                    checked={prompt.is_active === true}
                    onChange={(e) => 
                      setPrompt(prev => ({
                        ...prev,
                        is_active: e.target.checked
                      }))
                    }
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md border mb-6">
              <h2 className="text-xl font-semibold mb-4">Parâmetros de Geração</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormInput
                    label="Temperatura"
                    id="temperature"
                    name="temperature"
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={prompt.temperature?.toString() || "0.7"}
                    onChange={handleInputChange}
                    error={formErrors.temperature}
                    helperText="Controla a aleatoriedade das respostas (0-2)"
                    leftIcon={<i className="fas fa-thermometer-half text-gray-400 text-sm" />}
                  />
                </div>

                <div className="flex items-center h-full">
                  <FormCheckbox
                    id="with_rag"
                    name="with_rag"
                    label="Usar RAG (Retrieval Augmented Generation)"
                    checked={prompt.with_rag === true}
                    onChange={(e) =>
                      setPrompt(prev => ({
                        ...prev,
                        with_rag: e.target.checked
                      }))
                    }
                    helperText="Permite buscar conhecimento adicional da base de dados"
                  />
                </div>

                <div>
                  <FormSelect
                    label="Provedor do Modelo"
                    id="provider_name"
                    name="provider_name"
                    value={prompt.provider_name || 'ANTHROPIC'}
                    onChange={handleInputChange}
                    error={formErrors.provider_name}
                    options={[
                      { value: "ANTHROPIC", label: "Anthropic" },
                      { value: "OPENAI", label: "OpenAI" },
                      { value: "OLLAMA", label: "Ollama" },
                      { value: "UNKNOWN", label: "Outro" }
                    ]}
                    leftIcon={<i className="fas fa-robot text-gray-400 text-sm" />}
                    required
                  />
                </div>

                <div>
                  <FormInput
                    label="Nome do Modelo"
                    id="model_name"
                    name="model_name"
                    type="text"
                    value={prompt.model_name || ''}
                    onChange={handleInputChange}
                    error={formErrors.model_name}
                    helperText="Ex: claude-3-haiku-20240307, gpt-4, etc."
                    leftIcon={<i className="fas fa-microchip text-gray-400 text-sm" />}
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md border mb-6">
              <h2 className="text-xl font-semibold mb-4">Ferramentas Disponíveis</h2>
              
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-white min-h-[50px]">
                {prompt.tools?.map((tool, index) => (
                  <div key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center">
                    <span>{tool}</span>
                    <button 
                      type="button"
                      className="ml-1 text-blue-500 hover:text-blue-700"
                      onClick={() => {
                        setPrompt(prev => ({
                          ...prev,
                          tools: prev.tools?.filter((_, i) => i !== index)
                        }));
                      }}
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                  </div>
                ))}
                {(!prompt.tools || prompt.tools.length === 0) && (
                  <span className="text-gray-400 text-sm">Nenhuma ferramenta selecionada</span>
                )}
              </div>
              
              <div className="mt-2">
                <div className="flex flex-col sm:flex-row items-end gap-2 max-w-3xl">
                  <div className="w-full sm:w-2/3 md:w-3/5">
                    <label htmlFor="tool_select" className="block text-sm font-medium text-gray-700 mb-1">
                      Selecionar Ferramenta
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-1 flex items-center justify-center pointer-events-none h-full">
                        <span className="flex items-center">
                          <i className="fas fa-tools text-gray-400 text-sm"></i>
                        </span>
                      </div>
                      <select 
                        id="tool_select"
                        value={selectedTool}
                        onChange={(e) => setSelectedTool(e.target.value)}
                        className="appearance-none w-full pl-8 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Selecionar ferramenta --</option>
                        {availableTools
                          .filter(tool => !prompt.tools?.includes(tool))
                          .map(tool => (
                            <option key={tool} value={tool}>
                              {tool}
                            </option>
                          ))
                        }
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center justify-center pointer-events-none h-full">
                        <svg className="h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      if (selectedTool) {
                        setPrompt(prev => ({
                          ...prev,
                          tools: [...(prev.tools || []), selectedTool]
                        }));
                        setSelectedTool('');
                      }
                    }}
                    disabled={!selectedTool}
                    className="h-10 min-w-24 w-full sm:w-auto"
                  >
                    Adicionar
                  </Button>
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Ferramentas que o assistente pode utilizar para realizar tarefas
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md border mb-6">
              <h2 className="text-xl font-semibold mb-4">Parâmetros Disponíveis</h2>
              
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-white">
                {availableParameters.length === 0 ? (
                  <p className="text-sm text-gray-500">Carregando parâmetros...</p>
                ) : (
                  availableParameters.map((param, index) => (
                    <div 
                      key={index} 
                      className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs cursor-pointer hover:bg-blue-100"
                      onClick={() => {
                        setSelectedParameter(param.name);
                        insertParameter();
                      }}
                    >
                      {param.name}
                    </div>
                  ))
                )}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Clique em um parâmetro para inseri-lo no formato {'{parametro}'} na mensagem.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md border mb-6">
              <h2 className="text-xl font-semibold mb-4">Conteúdo da Mensagem</h2>
              
              <label 
                htmlFor="message" 
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mensagem Principal
              </label>
              <textarea
                id="message"
                name="message"
                rows={12}
                value={prompt.message || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setPrompt(prev => ({
                    ...prev,
                    message: value,
                    system_message: value  // Atualiza ambos os campos
                  }));
                  
                  // Limpar erro quando o campo é editado
                  if (formErrors.message) {
                    setFormErrors(prev => ({
                      ...prev,
                      message: ''
                    }));
                  }
                }}
                className={`w-full p-3 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono ${
                  formErrors.message ? 'border-red-300' : 'border-gray-300'
                } bg-white`}
                placeholder="Digite aqui o conteúdo da mensagem. Você pode usar variáveis no formato {variável}."
              />
              {formErrors.message && (
                <p className="mt-1 text-sm text-red-600">{formErrors.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Use a sintaxe {'{variavel}'} para conteúdo dinâmico. Esta mensagem será exibida para o usuário.
              </p>
            
              <div className="mt-4">
                <label
                  htmlFor="system_media"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Mensagem para Mídias (Opcional)
                </label>
                <textarea
                  id="system_media"
                  name="system_media"
                  rows={6}
                  value={prompt.system_media || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono border-gray-300 bg-white"
                  placeholder="Entre com instruções específicas para processamento de mídia (opcional)."
                />
                <p className="mt-1 text-sm text-gray-500">
                  Instruções específicas para processamento de conteúdo de mídia, como imagens e vídeos.
                </p>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="system_deferred_message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Mensagem para Represadas (Opcional)
                </label>
                <textarea
                  id="system_deferred_message"
                  name="system_deferred_message"
                  rows={6}
                  value={prompt.system_deferred_message || ''}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono border-gray-300 bg-white"
                  placeholder="Entre com instruções específicas para mensagens represadas (opcional)."
                />
                <p className="mt-1 text-sm text-gray-500">
                  Instruções específicas para quando existirem mensagens represadas no sistema.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/applications/${id}/prompts`)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Criando...' : 'Criar'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default PromptCreate;